/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { horizontalScrollbarStyles } from './themes/TimeComponentStyles.css';
import { TimeComponentScrollbar } from './TimeComponentScrollbar';

type Props = {
  thumbWidth: number;
  thumbLeft?: number;
  thumbRight?: number;
  draggableRegion: { xMin: number; xMax: number; yMin: number; yMax: number };
  getNewRange: (newThumbLeft: number) => { newMin: number; newMax: number };
  onRangeChange: (newMin: number, newMax: number) => void;
  width: number;
  height: number;
};

export function TimeComponentHorizontalScrollbar({ getNewRange, onRangeChange, ...props }: Props) {
  const prevRange = useRef({ min: 0, max: 0 });
  const processRange = (newMin: number, newMax: number) => {
    const { min: prevMin, max: prevMax } = prevRange.current;
    const isRangeChanged = newMin !== prevMin || newMax !== prevMax;
    isRangeChanged && onRangeChange(newMin, newMax);
    prevRange.current = { min: newMin, max: newMax };
  };

  const processDragRangeXChange = (x: number, thumbOffsetX: number) => {
    const { newMin, newMax } = getNewRange(Math.max(0, x - thumbOffsetX));
    processRange(newMin, newMax);
  };

  const processClickRangeXChange = (x: number) => {
    const { xMin, xMax } = props.draggableRegion;
    const halfThumbWidth = (xMax - xMin) / 2;
    const { newMin, newMax } = getNewRange(Math.max(0, x - halfThumbWidth));
    processRange(newMin, newMax);
  };

  return (
    <TimeComponentScrollbar
      {...props}
      processDragRangeXChange={processDragRangeXChange}
      processClickRangeXChange={processClickRangeXChange}
      styles={horizontalScrollbarStyles}
    />
  );
}
